import { Button, ButtonProps } from "@cmp-src/primitives/button";
import { LucideIcon } from "lucide-react";
import { cn } from "@gettactic/helpers";
import * as React from "react";

// Color variants for semantic icon buttons (table actions, etc.)
const colorVariants = {
  success: "text-green-600 hover:bg-green-50 hover:text-green-700",
  danger: "text-red-500 hover:bg-red-50 hover:text-red-700",
  neutral: "text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
};

type ColorVariant = keyof typeof colorVariants;

// Size presets for icon buttons
const sizePresets = {
  xs: { button: "h-6 w-6", icon: "h-3 w-3" },
  sm: { button: "h-8 w-8", icon: "h-4 w-4" },
  md: { button: "h-10 w-10", icon: "h-5 w-5" },
  lg: { button: "h-12 w-12", icon: "h-6 w-6" }
};

type SizePreset = keyof typeof sizePresets;

export interface IconButtonProps
  extends Omit<ButtonProps, "variant" | "size" | "children"> {
  /** LucideIcon component or custom React node */
  icon: LucideIcon | React.ReactNode;
  /**
   * Button variant - can be a Button variant (ghost, secondary, etc.)
   * or a color variant (success, danger, neutral) for semantic actions
   */
  variant?: ButtonProps["variant"] | ColorVariant;
  /** Size preset for the button */
  size?: SizePreset;
  /** Custom icon className */
  iconClassName?: string;
  /** For table/list action buttons with hover opacity effect */
  showOnHover?: boolean;
}

const isColorVariant = (
  variant: string | null | undefined
): variant is ColorVariant => {
  return typeof variant === "string" && variant in colorVariants;
};

const isLucideIcon = (
  icon: LucideIcon | React.ReactNode
): icon is LucideIcon => {
  if (typeof icon === "function") return true;
  // forwardRef components (like Lucide icons) are objects with $$typeof and render
  if (
    typeof icon === "object" &&
    icon !== null &&
    "$$typeof" in icon &&
    "render" in icon
  ) {
    return true;
  }
  return false;
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = "ghost",
      size = "sm",
      iconClassName,
      showOnHover = false,
      className,
      ...props
    },
    ref
  ) => {
    const sizeConfig = sizePresets[size];
    const isColor = isColorVariant(variant);
    const buttonVariant = isColor ? "ghost" : variant;

    const iconElement = isLucideIcon(icon)
      ? React.createElement(icon, {
          className: cn(sizeConfig.icon, iconClassName)
        })
      : React.isValidElement(icon)
        ? React.cloneElement(
            icon as React.ReactElement<{ className?: string }>,
            {
              className: cn(
                sizeConfig.icon,
                (icon as React.ReactElement<{ className?: string }>).props
                  ?.className,
                iconClassName
              )
            }
          )
        : icon;

    return (
      <Button
        ref={ref}
        variant={buttonVariant}
        size="icon"
        className={cn(
          sizeConfig.button,
          "p-0",
          showOnHover &&
            "opacity-30 transition-opacity duration-100 group-hover:opacity-100",
          isColor && colorVariants[variant],
          className
        )}
        {...props}
      >
        {iconElement}
      </Button>
    );
  }
);

IconButton.displayName = "IconButton";
